import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "../App";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { 
  Flower2, 
  ArrowLeft, 
  ArrowRight,
  Leaf,
  Sparkles,
  Flame,
  Heart,
  Sun,
  CheckCircle2,
  Loader2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const businessTypes = [
  { id: "home_fragrances", name: "Home Fragrances", icon: Flower2, description: "Scented products for the home" },
  { id: "dry_leaf_tea", name: "Dry Leaf Tea", icon: Leaf, description: "Artisanal tea blends" },
  { id: "soft_skincare", name: "Soft Skincare", icon: Sparkles, description: "Natural skincare products" },
  { id: "candles_aromatherapy", name: "Candles & Aromatherapy", icon: Flame, description: "Handcrafted candles" },
  { id: "natural_cosmetics", name: "Natural Cosmetics", icon: Heart, description: "Clean beauty products" },
  { id: "wellness_brands", name: "Wellness Brands", icon: Sun, description: "Holistic health products" }
];

const brandValues = [
  "Sustainability", "Thoughtful Sourcing", "Cruelty-Free", "Organic",
  "Women-Owned", "Small Batch", "Handcrafted", "Vegan",
  "Eco-Friendly", "Natural", "Mindful", "Wellness-Focused"
];

const stylePreferences = [
  "Minimalist", "Luxurious", "Bohemian", "Modern",
  "Vintage", "Earthy", "Elegant", "Playful",
  "Sophisticated", "Rustic", "Clean", "Warm"
];

const targetAudiences = [
  "Young Professionals (25-35)", "Wellness Enthusiasts", "Eco-Conscious Consumers",
  "Luxury Seekers", "Busy Mothers", "Self-Care Advocates",
  "Gift Buyers", "Health-Conscious Individuals"
];

const Questionnaire = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [currentStep, setCurrentStep] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [answers, setAnswers] = useState({
    business_type: "",
    values: [],
    target_audience: "",
    style_preferences: [],
    unique_selling_point: "",
    budget_range: "Starter ($1,000-$5,000)",
    experience_level: "Complete Beginner"
  });

  // Redirect to login if not authenticated
  React.useEffect(() => {
    if (!user) {
      // Store return URL for after login
      sessionStorage.setItem('checkoutReturnUrl', '/create');
      toast.info("Please sign in to create your brand");
      navigate("/");
    }
  }, [user, navigate]);

  const steps = [
    { title: "Business Type", subtitle: "What kind of business do you want to create?" },
    { title: "Core Values", subtitle: "Select values that resonate with your brand" },
    { title: "Target Audience", subtitle: "Who are your ideal customers?" },
    { title: "Style Preferences", subtitle: "What aesthetic defines your brand?" },
    { title: "Unique Selling Point", subtitle: "What makes your brand special?" }
  ];

  const progress = ((currentStep + 1) / steps.length) * 100;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate("/dashboard");
    }
  };

  const toggleArrayValue = (field, value) => {
    const current = answers[field];
    if (current.includes(value)) {
      setAnswers({ ...answers, [field]: current.filter(v => v !== value) });
    } else {
      if (current.length < 5) {
        setAnswers({ ...answers, [field]: [...current, value] });
      } else {
        toast.warning("Maximum 5 selections allowed");
      }
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return answers.business_type !== "";
      case 1: return answers.values.length >= 3;
      case 2: return answers.target_audience !== "";
      case 3: return answers.style_preferences.length >= 2;
      case 4: return answers.unique_selling_point.trim() !== "";
      default: return false;
    }
  };

  const handleGenerate = async () => {
    // Double-check authentication before generating
    if (!user) {
      sessionStorage.setItem('checkoutReturnUrl', '/create');
      toast.error("Please sign in to generate your brand");
      navigate("/");
      return;
    }
    
    setIsGenerating(true);
    try {
      const response = await axios.post(`${API}/generate-brand`, answers, {
        timeout: 120000 // 2 minute timeout for AI generation
      });
      navigate("/brand-results", { 
        state: { 
          brandData: response.data, 
          questionnaire: answers 
        } 
      });
    } catch (error) {
      console.error("Generation error:", error);
      if (error.response?.status === 401) {
        // Session expired - redirect to login
        sessionStorage.setItem('checkoutReturnUrl', '/create');
        toast.error("Your session has expired. Please sign in again.");
        navigate("/");
      } else if (error.code === 'ECONNABORTED') {
        toast.error("Brand generation is taking longer than expected. Please try again.");
      } else {
        toast.error(error.response?.data?.detail || "Failed to generate brand. Please try again.");
      }
      setIsGenerating(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {businessTypes.map((type) => {
              const IconComponent = type.icon;
              const isSelected = answers.business_type === type.id;
              return (
                <div
                  key={type.id}
                  onClick={() => setAnswers({ ...answers, business_type: type.id })}
                  className={`option-card ${isSelected ? 'selected' : ''} flex items-start gap-4`}
                  data-testid={`business-type-${type.id}`}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isSelected ? 'bg-[#C77D63]/10' : 'bg-[#F5F0EB]'}`}>
                    <IconComponent className={`w-6 h-6 ${isSelected ? 'text-[#C77D63]' : 'text-[#8DA399]'}`} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2C332E]">{type.name}</h3>
                    <p className="text-sm text-[#78716C]">{type.description}</p>
                  </div>
                  {isSelected && (
                    <CheckCircle2 className="w-5 h-5 text-[#C77D63] ml-auto" />
                  )}
                </div>
              );
            })}
          </div>
        );

      case 1:
        return (
          <div>
            <p className="text-sm text-[#78716C] mb-4">Select 3-5 values that align with your brand</p>
            <div className="flex flex-wrap gap-3">
              {brandValues.map((value) => {
                const isSelected = answers.values.includes(value);
                return (
                  <button
                    key={value}
                    onClick={() => toggleArrayValue('values', value)}
                    className={`px-4 py-2 rounded-full border-2 transition-colors duration-200 ${
                      isSelected 
                        ? 'border-[#C77D63] bg-[#C77D63]/10 text-[#C77D63]' 
                        : 'border-[#E6E1DB] hover:border-[#8DA399] text-[#2C332E]'
                    }`}
                    data-testid={`value-${value.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {value}
                  </button>
                );
              })}
            </div>
            <p className="text-sm text-[#78716C] mt-4">Selected: {answers.values.length}/5</p>
          </div>
        );

      case 2:
        return (
          <div className="grid md:grid-cols-2 gap-4">
            {targetAudiences.map((audience) => {
              const isSelected = answers.target_audience === audience;
              return (
                <div
                  key={audience}
                  onClick={() => setAnswers({ ...answers, target_audience: audience })}
                  className={`option-card ${isSelected ? 'selected' : ''} flex items-center justify-between`}
                  data-testid={`audience-${audience.toLowerCase().replace(/[\s()]/g, '-')}`}
                >
                  <span className="text-[#2C332E]">{audience}</span>
                  {isSelected && <CheckCircle2 className="w-5 h-5 text-[#C77D63]" />}
                </div>
              );
            })}
          </div>
        );

      case 3:
        return (
          <div>
            <p className="text-sm text-[#78716C] mb-4">Select 2-5 style preferences</p>
            <div className="flex flex-wrap gap-3">
              {stylePreferences.map((style) => {
                const isSelected = answers.style_preferences.includes(style);
                return (
                  <button
                    key={style}
                    onClick={() => toggleArrayValue('style_preferences', style)}
                    className={`px-4 py-2 rounded-full border-2 transition-colors duration-200 ${
                      isSelected 
                        ? 'border-[#C77D63] bg-[#C77D63]/10 text-[#C77D63]' 
                        : 'border-[#E6E1DB] hover:border-[#8DA399] text-[#2C332E]'
                    }`}
                    data-testid={`style-${style.toLowerCase()}`}
                  >
                    {style}
                  </button>
                );
              })}
            </div>
            <p className="text-sm text-[#78716C] mt-4">Selected: {answers.style_preferences.length}/5</p>
          </div>
        );

      case 4:
        return (
          <div className="max-w-xl">
            <Textarea
              placeholder="What makes your brand unique? What problem do you solve? What's your story?"
              value={answers.unique_selling_point}
              onChange={(e) => setAnswers({ ...answers, unique_selling_point: e.target.value })}
              className="min-h-[150px] bg-white/50 border-[#E6E1DB] focus:border-[#8DA399] rounded-xl resize-none"
              data-testid="usp-input"
            />
            <p className="text-sm text-[#78716C] mt-2">
              Be specific about what makes your brand different from competitors.
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Navigation */}
      <nav className="nav-glass sticky top-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={handleBack}
            className="flex items-center gap-2 text-[#2C332E] hover:bg-[#F5F0EB]"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </Button>
          <div className="flex items-center">
            <span className="text-xl font-semibold text-[#2C332E]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Bloomwright Studio
            </span>
          </div>
          <div className="w-20" /> {/* Spacer for centering */}
        </div>
      </nav>

      {/* Progress Bar */}
      <div className="max-w-3xl mx-auto px-6 pt-8">
        <Progress value={progress} className="h-2 bg-[#E6E1DB]" />
        <div className="flex justify-between mt-2">
          <span className="text-sm text-[#78716C]">Step {currentStep + 1} of {steps.length}</span>
          <span className="text-sm text-[#8DA399] font-medium">{Math.round(progress)}% Complete</span>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Step Header */}
        <div className="text-center mb-12">
          <p className="text-[#C77D63] font-accent text-xl mb-2">{steps[currentStep].subtitle}</p>
          <h1 className="text-4xl md:text-5xl tracking-tight text-[#2C332E]">
            {steps[currentStep].title}
          </h1>
        </div>

        {/* Step Content */}
        <div className="mb-12">
          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <Button 
            variant="outline"
            onClick={handleBack}
            className="border-[#E6E1DB] hover:bg-[#F5F0EB] text-[#2C332E] rounded-full px-6 py-3"
            data-testid="prev-btn"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Previous
          </Button>
          
          {currentStep < steps.length - 1 ? (
            <Button 
              onClick={handleNext}
              disabled={!canProceed()}
              className="bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="next-btn"
            >
              Next
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          ) : (
            <Button 
              onClick={handleGenerate}
              disabled={!canProceed() || isGenerating}
              className="bg-[#8DA399] hover:bg-[#7A8F86] text-white rounded-full px-8 py-3 disabled:opacity-50"
              data-testid="generate-btn"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Generating Your Brand...
                </>
              ) : (
                <>
                  Generate My Brand
                  <Sparkles className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          )}
        </div>

        {/* Generating Overlay */}
        {isGenerating && (
          <div className="fixed inset-0 bg-[#2C332E]/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl">
              <div className="w-16 h-16 rounded-full bg-[#8DA399]/10 flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-8 h-8 text-[#8DA399] animate-spin" />
              </div>
              <h3 className="text-xl font-semibold text-[#2C332E] mb-2">Creating Your Brand</h3>
              <p className="text-[#78716C] mb-4">
                Our AI is crafting your unique brand identity, including name suggestions, color palette, and starter formula...
              </p>
              <p className="text-sm text-[#8DA399]">This may take up to a minute</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Questionnaire;
